import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../../core/services/api.service';
import { SendManualPlateCorrectionPayload } from '../../../../../core/models/ev.model';
import { ToastService } from 'angular-toastify';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-ev-charger-license-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-ev-charger-license-edit.component.html',
  styleUrl: './modal-ev-charger-license-edit.component.css'
})
export class ModalEvChargerLicenseEditComponent implements OnChanges {
  @Input() isOpen: boolean = false;
  @Input() data: {
    historyUid?: string;
    licensePlate?: string;
    siteEvName?: string;
    spaceName?: string;
    userId?: number;
    imageSrc?: string | null;
    transactionEvId?: number;
  } | null = null;
  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<string>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  isLoading = false;
  newLicensePlate = '';
  /** true only after we confirmed imageSrc exists and <img> loaded successfully */
  showImage = false;

  isZoomOpen = false;
  zoomScale = 1;
  readonly zoomMin = 1;
  readonly zoomMax = 4;
  readonly zoomStep = 0.25;

  constructor(
    private apiService: ApiService,
    private toast: ToastService
  ) { }

  ngOnChanges(): void {
    if (this.isOpen && this.data) {
      this.newLicensePlate = this.data.licensePlate || '';
      this.showImage = false;
      this.closeZoom();
    }
    if (!this.isOpen) {
      this.showImage = false;
      this.closeZoom();
    }
  }

  get previewImageSrc(): string | null {
    return this.hasImageSrc(this.data?.imageSrc) ? (this.data!.imageSrc as string).trim() : null;
  }

  onImageLoad(): void {
    this.showImage = true;
  }

  onImageError(): void {
    this.showImage = false;
    this.closeZoom();
  }

  openZoom(): void {
    if (!this.showImage || !this.previewImageSrc) return;
    this.zoomScale = 1;
    this.isZoomOpen = true;
  }

  closeZoom(): void {
    this.isZoomOpen = false;
    this.zoomScale = 1;
  }

  zoomIn(): void {
    this.zoomScale = Math.min(this.zoomMax, +(this.zoomScale + this.zoomStep).toFixed(2));
  }

  zoomOut(): void {
    this.zoomScale = Math.max(this.zoomMin, +(this.zoomScale - this.zoomStep).toFixed(2));
  }

  resetZoom(): void {
    this.zoomScale = 1;
  }

  onZoomWheel(event: WheelEvent): void {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  closeModal() {
    this.isOpen = false;
    this.showImage = false;
    this.closeZoom();
    this.onClose.emit();
  }

  save() {
    const data = this.data;
    if (!data?.historyUid) {
      this.toast.error('ไม่พบข้อมูลสำหรับแก้ไขทะเบียน');
      return;
    }

    this.isLoading = true;
    const payload: SendManualPlateCorrectionPayload = {
      historyUid: data.historyUid,
      licensePlate: this.newLicensePlate.trim(),
      siteEvName: data.siteEvName ?? '',
      spaceName: data.spaceName ?? '',
      userId: data.userId ?? 0,
    };

    this.apiService.sentManualPlateCorrection(payload).subscribe({
      next: (res) => {
        if (res.status === 'ok') {
          this.onSave.emit(this.newLicensePlate);
          this.closeModal();
        } else {
          this.toast.error('เกิดข้อผิดพลาด: ' + (res.error?.message || 'ไม่ทราบสาเหตุ'));
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Update License Plate Error:', err);
        this.toast.error('ไม่สามารถบันทึกข้อมูลได้');
        this.isLoading = false;
      }
    });
  }

  private hasImageSrc(value: string | null | undefined): boolean {
    return !!value && value.trim().length > 0;
  }

  get canShowActivityHistory(): boolean {
    return (this.data?.transactionEvId ?? 0) > 0;
  }

  get activityEntityId(): number {
    return this.data?.transactionEvId ?? 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
